import React, { useContext, useEffect, useState } from 'react';
import { Container, Row } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import { Formik, FormikHelpers, FormikProps } from 'formik';
import * as Yup from 'yup';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Button,
  ConfirmModal,
  PageHeader,
  Section,
  SpinnerArea,
} from '../../../../shared/components';
import OperationContext from '../context';
import CalculatorContext from './CalculatorContext';
import MainContext from '../../../App/MainProvider/context';
import OperationSubtitle from '../OperationSubtitle';
import useQuery from '../../../../shared/hooks/useQuery';
import Role from '../../../../types/role';
import EmptyHistory from './EmptyHistory';
import VersionTable from './VersionTable';
import { Calculation, CalculatorRowDto } from '../../../../services/types/Calculator';
import FormDropdown from '../../../../shared/components/form/FormDropdown';
import { OptionValue } from '../../../../services/types/Misc';
import calculatorService from '../../../../services/calculatorService';
import useService from '../../../../shared/hooks/useService';
import { BasicNotification } from '../../../../shared/components/Notification';

interface FormState {
  sector: OptionValue | null;
  country: OptionValue | null;
  unit: OptionValue | null;
}

const unitOptions: OptionValue[] = [
  { id: 2, text: 'Imperial', value: 'Imperial' },
  { id: 1, text: 'Metric', value: 'Metric' },
];

const validationSchema = Yup.object().shape({
  sector: Yup.object().nullable().required('Sector is required'),
  country: Yup.object().nullable().required('Country is required'),
  unit: Yup.object().nullable().required('Unit is required'),
});

const Calculator: React.FC = () => {
  const { operation, loading } = useContext(OperationContext);

  const {
    sectors,
    countries,
    enable,
    lastVersion,
    blocked,
    stepBlockedBy,
    flowTypeBlockedBy,
    unitId: contextUnitId,
    isMember,
  } = useContext(CalculatorContext);
  const { t } = useTranslation();
  const { inSuperUserMode, inGuidanceServiceMode, user, notify, service } = useContext(MainContext);
  const isViewer = user.roleCode === Role.Viewer;
  const isAdmin = user.roleCode === 'ADMIN' || user.secondRoleCode === 'ADMIN';
  const canEdit =
    operation.isMember || inSuperUserMode() || (operation.belongsGS && inGuidanceServiceMode());

  const canDelete = isMember || isAdmin || inSuperUserMode();

  const [currentVersion, setCurrentVersion] = useState(lastVersion);

  const [formDirty, setFormDirty] = useState(false);
  const [showCurrentVersion, setShowCurrentVersion] = useState(false);
  const [spinnerLoading, setSpinnerLoading] = useState(false);

  const queryParams = useQuery();
  const from = queryParams.get('from') || undefined;
  const title = `Operation ${operation.number} / GHG Calculator`;

  const limitRows = 6;
  const countriesList = countries.map((x) => ({ id: x.id, text: x.name, value: x.name }));
  const sectorsOptions = sectors.map((x) => ({ id: x.id, text: x.name, value: x.name }));

  const [, setLimitCalculators] = useState(false);

  const [versionInprogress, setVersionInprogress] = useState(false);

  const [currentVersionRows, setCurrentVersionRows] = useState<Calculation[] | null>(null);
  const [otherVersionRows, setOtherVersionRows] = useState<Calculation[] | null>(null);

  const [, setLasUnitSystem] = useState<string | null>(null);

  const [visible] = useState(false);

  type ShowingVersionsType = {
    [key: number]: boolean;
  };

  const [showingVersions, setShowingVersions] = useState<ShowingVersionsType>({});

  const [calculatorRows, loading1, reloadData] = useService(async () =>
    calculatorService.getRows(operation.id)
  );

  const initialUnit = unitOptions.find((option) => option.id === contextUnitId) || null;

  const isSentLastCalculator =
    currentVersionRows !== null &&
    currentVersionRows.some((calculator) => calculator.sent === true);

  const [disableSelectParameters, setDisableSelectParameters] = useState(
    !canEdit || isSentLastCalculator || blocked
  );
  useEffect(() => {
    if (calculatorRows != null && calculatorRows.length >= limitRows) {
      setLimitCalculators(true);
    } else {
      setLimitCalculators(false);
    }

    if (calculatorRows != null && calculatorRows.length > 0) {
      setVersionInprogress(!isSentLastCalculator);
      setShowCurrentVersion(true);
    }

    if (currentVersion === 0) {
      setCurrentVersion(1);
    }

    if (calculatorRows != null && calculatorRows.length > 0 && currentVersion) {
      const current = calculatorRows.filter((x) => x.version === currentVersion);
      setCurrentVersionRows(current);
      const others = calculatorRows.filter((x) => x.version !== currentVersion);
      setOtherVersionRows(others);
    } else {
      setCurrentVersionRows([]);
      setOtherVersionRows([]);
    }
  }, [calculatorRows, currentVersion, loading1, isSentLastCalculator]);

  useEffect(() => {
    if (calculatorRows != null && calculatorRows.length > 0) {
      setLasUnitSystem(calculatorRows[0].unitId.toString());
    }
  }, [calculatorRows]);

  useEffect(() => {
    setDisableSelectParameters(!canEdit || isSentLastCalculator || blocked);
  }, [isSentLastCalculator, canEdit, blocked]);

  const handleSubmit = async (values: FormState, formikHelpers: FormikHelpers<FormState>) => {
    setSpinnerLoading(true);

    const payload: CalculatorRowDto = {
      operationId: operation.id,
      sectorId: values.sector && values.sector.id ? Number(values.sector.id) : 0,
      countryId: values.country && values.country.id ? Number(values.country.id) : 0,
      unitId: values.unit && values.unit.id ? Number(values.unit.id) : 0,
    };

    try {
      await service(() => calculatorService.addCalculatorRow(payload), {
        callback: async () => {
          notify('Calculator', 'Row added successfully', 'success');
          formikHelpers.resetForm();
          setFormDirty(false);

          const updatedRows = await calculatorService.getRows(operation.id);
          reloadData(updatedRows);
        },
      });
    } catch (error) {
      notify('Error', 'Failed to add row', 'error');
      // eslint-disable-next-line no-console
      console.error('Error adding row:', error);
    } finally {
      setSpinnerLoading(false);
    }
  };

  const newFromRecent = async () => {
    try {
      setSpinnerLoading(true);
      await service(() => calculatorService.createFromMostRecent(operation.id), {
        callback: async () => {
          notify('Calculator', 'Row added successfully', 'success');
          setFormDirty(false);
          const updatedRows = await calculatorService.getRows(operation.id);
          reloadData(updatedRows);
          setTimeout(() => {
            window.location.reload();
          }, 100);
        },
      });
    } catch (error) {
      notify('Error', 'Failed to create from recent', 'error');
      // eslint-disable-next-line no-console
      console.error('Error creating from recent:', error);
    } finally {
      setSpinnerLoading(false);
    }
  };

  const handleNewVersion = () => {
    if (!showCurrentVersion) {
      setShowCurrentVersion(!showCurrentVersion);
    } else if (isSentLastCalculator) {
      setCurrentVersion(currentVersion + 1);
    }
  };

  const [showConfirmDelete, setshowConfirmDelete] = useState(false);
  const [showConfirmSendCalculator, setshowConfirmSendCalculator] = useState(false);
  const [rowForDelete, setRowForDelete] = useState(0);

  const handleDeleteClick = (rowId: number) => {
    if (calculatorRows) {
      const row = calculatorRows.find((x) => x.id === rowId);
      if (!row) {
        return;
      }

      setRowForDelete(rowId);
      setshowConfirmDelete(true);
    }
  };

  const handleSendClick = () => {
    if (calculatorRows) {
      setshowConfirmSendCalculator(true);
    }
  };

  const handleCalculateClick = (rowId: number) => {
    if (calculatorRows) {
      const row = calculatorRows.find((x) => x.id === rowId);
      if (!row) {
        return;
      }

      setRowForDelete(rowId);
      setshowConfirmDelete(true);
    }
  };

  const calculatorInfo = (
    <FontAwesomeIcon
      icon="info-circle"
      title={t('calculatorInfoDisclaimer')}
      className="lastUpdateInfo"
    />
  );

  return (
    <>
      <Container className="rolesAndPermissions body-calculator-color">
        <PageHeader title={title} link={calculatorInfo} backUrl={from}>
          <OperationSubtitle />
        </PageHeader>
        <SpinnerArea loading={loading || loading1 || spinnerLoading}>
          <Row>
            {!enable && (
              <Section>
                {!loading &&
                  !loading1 &&
                  (canEdit ? (
                    <div className="m-3 d-flex justify-content-center">
                      <h1 className="page-title inner-title">
                        {t('operationNoToolkitCalculator', { number: operation.number })}
                      </h1>
                    </div>
                  ) : (
                    <div className="m-3 d-flex justify-content-center">
                      <h1 className="page-title inner-title">
                        {t('operationNoToolkitCalculatorNoMember', {
                          number: operation.number,
                        })}
                      </h1>
                    </div>
                  ))}
              </Section>
            )}
            {enable && !canEdit && !showCurrentVersion && !loading && !loading1 && (
              <Section>
                <div className="m-3 d-flex justify-content-center">
                  <h1 className="page-title inner-title">
                    {t('operationNoToolkitCalculatorNoMember', {
                      number: operation.number,
                    })}
                  </h1>
                </div>
              </Section>
            )}
            {enable && !loading && !loading1 && (showCurrentVersion || canEdit) && (
              <>
                <Section
                  title={
                    <>
                      <h5>{t('currentVersion')}</h5>
                      <div>
                        <Button
                          key="btnNewVersionFromCurrent"
                          className="btn btn-primary px-4 py-1"
                          onClick={newFromRecent}
                          text={t('esrrNewFromMostRecent')}
                          disabled={
                            !canEdit ||
                            versionInprogress ||
                            calculatorRows === null ||
                            calculatorRows.length === 0 ||
                            blocked
                          }
                        />
                        <Button
                          key="btnNewVersion"
                          className="btn btn-secondary px-4 py-1"
                          text={t('New')}
                          disabled={!canEdit || versionInprogress || blocked}
                          onClick={handleNewVersion}
                        />
                      </div>
                    </>
                  }
                >
                  {blocked && !isViewer && (
                    <div className="notification-wrapper-100 d-flex justify-content-center mb-3">
                      <BasicNotification
                        message={t('flowIndicatorsEdit', {
                          flowTypeAn: t(`stepBlockedByAn.${flowTypeBlockedBy}`),
                          step: t(`flowStatusCodes.${stepBlockedBy}`),
                          flowType: t(`stepBlockedBy.${flowTypeBlockedBy}`),
                        })}
                        color="warning"
                        icon="exclamation-triangle"
                      />
                    </div>
                  )}
                  {showCurrentVersion && (
                    <>
                      <hr />
                      <Formik<FormState>
                        initialValues={{ sector: null, country: null, unit: initialUnit }}
                        validationSchema={validationSchema}
                        onSubmit={handleSubmit}
                      >
                        {({
                          setFieldValue,
                          values,
                          submitForm,
                          isValid,
                        }: FormikProps<FormState>) => (
                          <>
                            <h4>{t('Select Parameters')}</h4>
                            <div className="form-row align-items-center calculator-forms">
                              <div className="col-sm-1 d-flex align-items-center">
                                <label className="col-form-label mr-2" htmlFor="sector">
                                  {t('Sector')}
                                </label>
                              </div>
                              <div className="col-sm-4 d-flex align-items-center">
                                <FormDropdown
                                  name="sector"
                                  elements={sectorsOptions}
                                  value={
                                    values.sector && values.sector.value ? values.sector.value : ''
                                  }
                                  className="no-modal dropdown-calculator"
                                  onChangeAction={(selectedValue) => {
                                    setFieldValue(
                                      'sector',
                                      sectorsOptions.find(
                                        (option) => option.value === selectedValue
                                      ) || null
                                    );
                                    setFormDirty(true);
                                  }}
                                  disabled={disableSelectParameters}
                                />
                              </div>
                              <div className="col-sm-1 d-flex align-items-center">
                                <label className="col-form-label mr-2" htmlFor="country">
                                  {t('country')}
                                </label>
                              </div>
                              <div className="col-sm-4 d-flex align-items-center">
                                <FormDropdown
                                  name="country"
                                  elements={countriesList}
                                  value={
                                    values.country && values.country.value
                                      ? values.country.value
                                      : ''
                                  }
                                  className="no-modal dropdown-calculator"
                                  onChangeAction={(selectedValue) => {
                                    setFieldValue(
                                      'country',
                                      countriesList.find(
                                        (option) => option.value === selectedValue
                                      ) || null
                                    );
                                    setFormDirty(true);
                                  }}
                                  disabled={disableSelectParameters}
                                />
                              </div>
                              {visible && (
                                <>
                                  <div className="col-sm-1 d-flex align-items-center">
                                    <label className="col-form-label mr-2" htmlFor="unit">
                                      {t('Units')}
                                    </label>
                                  </div>
                                  <div className="col-sm-2 d-flex align-items-center">
                                    <FormDropdown
                                      name="unit"
                                      elements={unitOptions}
                                      value={
                                        values.unit && values.unit.value ? values.unit.value : ''
                                      }
                                      className="no-modal dropdown-calculator"
                                      disabled
                                    />
                                  </div>
                                </>
                              )}
                              <div className="col-sm-2">
                                <Button
                                  key="btnNewVersion"
                                  className="btn btn-primary px-4 py-1"
                                  style={{ float: 'right' }}
                                  text={t('Add Sector')}
                                  onClick={submitForm}
                                  disabled={disableSelectParameters || !formDirty || !isValid}
                                />
                              </div>
                            </div>
                          </>
                        )}
                      </Formik>
                      <br />
                      <br />
                      <VersionTable
                        operation={operation}
                        className="last-version-content-calculator"
                        calculators={currentVersionRows != null ? currentVersionRows : []}
                        handleCalculateClick={
                          canEdit && !blocked ? handleCalculateClick : undefined
                        }
                        handleDeleteClick={canDelete && !blocked ? handleDeleteClick : undefined}
                        handleSendClick={canEdit && !blocked ? handleSendClick : undefined}
                        isbloked={blocked}
                        setLoadingSpinner={setSpinnerLoading}
                      />
                    </>
                  )}
                </Section>
                <Section title={t('history')}>
                  {otherVersionRows && otherVersionRows.length > 0 ? (
                    <>
                      {(() => {
                        const sections = [];
                        for (let version = currentVersion - 1; version > 0; version -= 1) {
                          const isVisible = showingVersions[version] || false;
                          const chevron = isVisible ? 'chevron-up' : 'chevron-down';

                          sections.push(
                            <div className="ml-3" key={version}>
                              <Button
                                className="btn-tertiary pl-1"
                                iconRight={chevron}
                                text={t(`GHG Calculator V${version}`)}
                                type="button"
                                onClick={() =>
                                  setShowingVersions((prev) => ({
                                    ...prev,
                                    [version]: !isVisible,
                                  }))
                                }
                              />
                              {isVisible && calculatorRows && (
                                <VersionTable
                                  operation={operation}
                                  className="last-version-content-calculator"
                                  calculators={calculatorRows.filter((x) => x.version === version)}
                                  setLoadingSpinner={setSpinnerLoading}
                                />
                              )}
                            </div>
                          );
                        }
                        return sections;
                      })()}
                    </>
                  ) : (
                    <EmptyHistory />
                  )}
                </Section>
              </>
            )}
          </Row>
        </SpinnerArea>
      </Container>
      <ConfirmModal
        visible={showConfirmDelete}
        buttonText={t('confirm')}
        setVisible={setshowConfirmDelete}
        action={() => {
          setSpinnerLoading(true);
          service(() => calculatorService.deleteRow(rowForDelete), {
            callback: () => {
              notify(t(`Calculator`), t(`Delete Calculate Success`), 'success');
            },
          }).finally(async () => {
            const updatedRows = await calculatorService.getRows(operation.id);
            reloadData(updatedRows);
            setSpinnerLoading(false);
          });
        }}
      >
        {t('confirmDeleteCalculateMessage')}
      </ConfirmModal>
      <ConfirmModal
        visible={showConfirmSendCalculator}
        buttonText={t('Send')}
        setVisible={setshowConfirmSendCalculator}
        action={() => {
          setSpinnerLoading(true);
          service(() => calculatorService.sendCalculator(operation.id), {
            callback: () => {
              notify(t(`Calculator`), t(`Send Success`), 'success');
            },
          }).finally(async () => {
            const updatedRows = await calculatorService.getRows(operation.id);
            reloadData(updatedRows);
            setSpinnerLoading(false);
          });
        }}
      >
        {t('sendCalculatorPopupMsg')}
      </ConfirmModal>
    </>
  );
};

export default Calculator;
